import React, { useState, useEffect, useRef } from 'react';
import { 
  Tabs, Table, Button, Modal, Form, Input, 
  message, Upload, Switch, Select, Tag, Alert, 
  Space, Avatar, Row, Col, Divider, Typography 
} from 'antd';
import { Helmet } from 'react-helmet';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  UploadOutlined, 
  LinkOutlined,
  MailOutlined,
  PhoneOutlined,
  FacebookOutlined,
  TwitterOutlined,
  InstagramOutlined,
  LinkedinOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import api from '../../services/api';

// React 19 compatibility patch
if (!React.findDOMNode) {
  React.findDOMNode = (node) => {
    if (node?.current) return node.current;
    return node instanceof HTMLElement ? node : null;
  };
}

const { TabPane } = Tabs;
const { Option } = Select;
const { Title, Text } = Typography;

const contentTypes = [
  'privacy_policy',
  'careers',
  'faqs',
  'team_members',
  'terms_conditions',
    'refund_policy',
  'newsletters',
  'social_links',
  'videos',
  'testimonials',
  'blogs',
  'banners'
];
const BASE_UPLOAD_URL = 'https://www.startupbusinesspartners.com/uploads/cms/';

const ContentManagement = () => {
  const [activeTab, setActiveTab] = useState(contentTypes[0]);
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [quillContent, setQuillContent] = useState('');
  const [users, setUsers] = useState([]);
  const tableRef = useRef(null);

  useEffect(() => {
    fetchContent(activeTab);
    if (activeTab === 'testimonials') {
      fetchUsers();
    }
  }, [activeTab]);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const fetchContent = async (type) => {
    setLoading(true);
    try {
      const response = await api.get(`/content/${type}`);
      if (response.data.success) {
        setContent(prev => ({ ...prev, [type]: response.data.data || [] }));
      } else {
        setErrorMsg(`Failed to fetch ${type}`);
        setContent(prev => ({ ...prev, [type]: [] }));
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setErrorMsg(`Failed to fetch ${type}: ${error.response?.data?.message || error.message}`);
      setContent(prev => ({ ...prev, [type]: [] }));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const uploadProps = {
    name: 'image',
    action: `${api.defaults.baseURL}/content/upload`,
    headers: {
      authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    fileList,
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('Image must be smaller than 5MB!');
        return false;
      }
      return true;
    },
    onChange: (info) => {
      setFileList(info.fileList);
      if (info.file.status === 'uploading') {
        setUploading(true);
        return;
      }
      if (info.file.status === 'done') {
        setUploading(false);
        if (info.file.response?.success) {
          message.success('File uploaded successfully!');
          const fieldName = ['testimonials', 'team_members'].includes(activeTab) 
            ? 'profile_image' 
            : 'image_url';
          form.setFieldsValue({ [fieldName]: info.file.response.filename });
        }
      }
      if (info.file.status === 'error') {
        setUploading(false);
        message.error('Upload failed!');
      }
    },
    onRemove: () => {
      const fieldName = ['testimonials', 'team_members'].includes(activeTab) 
        ? 'profile_image' 
        : 'image_url';
      form.setFieldsValue({ [fieldName]: null });
      setFileList([]);
    },
  };

  const handleSubmit = async () => {
    try {
      setSuccessMsg('');
      setErrorMsg('');
      const values = await form.validateFields();
      setLoading(true);

      values.is_active = values.is_active ? 1 : 0;
      if (values.is_published !== undefined) {
        values.is_published = values.is_published ? 1 : 0;
      }

      if (values.tags && Array.isArray(values.tags)) {
        values.tags = JSON.stringify(values.tags);
      }
      if (values.social_links && typeof values.social_links === 'object') {
        values.social_links = JSON.stringify(values.social_links);
      }

      const endpoint = editingId 
        ? `/content/${activeTab}/${editingId}`
        : `/content/${activeTab}`;
      const method = editingId ? 'put' : 'post';

      const response = await api[method](endpoint, values);

      if (response.data.success) {
        setOpen(false);
        form.resetFields();
        setFileList([]);
        setEditingId(null);
        setQuillContent('');
        setSuccessMsg(`${capitalize(activeTab.replace(/_/g, ' '))} ${editingId ? 'updated' : 'created'} successfully`);
        await fetchContent(activeTab);
      } else {
        setErrorMsg(response.data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setErrorMsg(error.response?.data?.message || error.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    const initialValues = {
      ...item,
      is_active: item.is_active === 1,
      is_published: item.is_published === 1
    };

    if (item.tags) {
      initialValues.tags = JSON.parse(item.tags);
    }
    if (item.social_links) {
      initialValues.social_links = JSON.parse(item.social_links);
    }

    if (['privacy_policy', 'terms_conditions'].includes(activeTab)) {
      setQuillContent(item.content || '');
    }

    form.setFieldsValue(initialValues);
    
    if (item.image_url || item.profile_image) {
      const fileUrl = item.image_url || item.profile_image;
      setFileList([{
        uid: '-1',
        name: fileUrl.split('/').pop(),
        status: 'done',
        url: `${BASE_UPLOAD_URL}${fileUrl.split('uploads/').pop()}`
      }]);
    } else {
      setFileList([]);
    }
    
    setEditingId(item.id);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      setSuccessMsg('');
      setErrorMsg('');
      
      const response = await api.delete(`/content/${activeTab}/${id}`);
      
      if (response.data.success) {
        setSuccessMsg(`${capitalize(activeTab.replace(/_/g, ' '))} deleted successfully`);
        await fetchContent(activeTab);
      } else {
        setErrorMsg(response.data.message || `Failed to delete ${activeTab.replace(/_/g, ' ')}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      setErrorMsg(
        error.response?.data?.message || 
        error.message || 
        `Failed to delete ${activeTab.replace(/_/g, ' ')}`
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      if (response.data.success) {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    }
  };

  const getColumns = () => {
    const baseColumns = [
      {
        title: 'S.No.',
        key: 'sno',
        width: 80,
        render: (_text, _record, index) => index + 1,
      },
      {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
        ellipsis: true
      },
      {
        title: 'Status',
        dataIndex: 'is_active',
        key: 'status',
        width: 100,
        render: (active) => (
          <Tag color={active ? 'green' : 'red'}>
            {active ? 'Active' : 'Inactive'}
          </Tag>
        )
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 200,
        render: (_, record) => (
          <Space size="small">
            <Button size="small" onClick={() => handleEdit(record)}>
              Edit
            </Button>
            {!['privacy_policy', 'terms_conditions', 'refund_policy'].includes(activeTab) && (
              <Button 
                size="small" 
                danger 
                onClick={() => handleDelete(record.id)}
                loading={loading}
              >
                Delete
              </Button>
            )}
          </Space>
        )
      }
    ];

    if (['testimonials', 'blogs', 'banners', 'team_members'].includes(activeTab)) {
      baseColumns.splice(1, 0, {
        title: 'Image',
        dataIndex: activeTab === 'testimonials' || activeTab === 'team_members' ? 'profile_image' : 'image_url',
        key: 'image',
        width: 80,
        render: (url) => (
          url ? <Avatar size={40} src={`${BASE_UPLOAD_URL}${url.split('uploads/').pop()}`} /> : null
        )
      });
    }

    switch (activeTab) {
      case 'careers':
        baseColumns.splice(1, 0,
          {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
            width: 150
          },
          {
            title: 'Type',
            dataIndex: 'job_type',
            key: 'type',
            width: 100
          }
        );
        break;
      case 'faqs':
        baseColumns.splice(1, 0, {
          title: 'Category',
          dataIndex: 'category',
          key: 'category',
          width: 150
        });
        break;
      case 'team_members':
        baseColumns.splice(1, 0,
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 150
          },
          {
            title: 'Position',
            dataIndex: 'position',
            key: 'position',
            width: 150
          }
        );
        break;
      case 'social_links':
        baseColumns.splice(1, 0,
          {
            title: 'Platform',
            dataIndex: 'platform',
            key: 'platform',
            width: 150
          },
          {
            title: 'URL',
            dataIndex: 'url',
            key: 'url',
            ellipsis: true,
            render: (url) => (
              <a href={url} target="_blank" rel="noopener noreferrer">
                {url}
              </a>
            )
          }
        );
        break;
      case 'videos':
        baseColumns.splice(1, 0,
          {
            title: 'Video URL',
            dataIndex: 'video_url',
            key: 'video_url',
            ellipsis: true,
            render: (url) => (
              <a href={url} target="_blank" rel="noopener noreferrer">
                <PlayCircleOutlined /> {url}
              </a>
            )
          }
        );
        break;
      case 'testimonials':
        baseColumns.splice(2, 0,
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 150
          },
          // REMOVED: designation column
          // REMOVED: company column  
          // REMOVED: user column
          {
            title: 'Website Live Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
              if (!status) return <Tag>N/A</Tag>;
              let color = '';
              switch (status.toLowerCase()) {
                case 'approved': color = 'green'; break;
                case 'rejected': color = 'red'; break;
                case 'pending': color = 'orange'; break;
                default: color = 'default';
              }
              return <Tag color={color}>{status.toUpperCase()}</Tag>;
            }
          }
        );
        break;
      case 'blogs':
        baseColumns.splice(2, 0,
          {
            title: 'Author',
            dataIndex: 'author',
            key: 'author',
            width: 150
          },
          {
            title: 'Published',
            dataIndex: 'is_published',
            key: 'published',
            width: 100,
            render: published => (
              <Tag color={published ? 'green' : 'orange'}>
                {published ? 'Published' : 'Draft'}
              </Tag>
            )
          }
        );
        break;
      case 'banners':
        baseColumns.splice(2, 0, {
          title: 'Position',
          dataIndex: 'position',
          key: 'position',
          width: 100
        });
        break;
      default:
        break;
    }

    return baseColumns;
  };

  const getFormFields = () => {
    const commonFields = [
      <Form.Item
        name="title"
        label="Title"
        key="title"
        rules={[{ required: true, message: 'Please input the title!' }]}
      >
        <Input />
      </Form.Item>,
      <Form.Item
        name="is_active"
        label="Active"
        key="is_active"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
    ];

    switch (activeTab) {
      case 'privacy_policy':
      case 'refund_policy': 
      case 'terms_conditions':
        return [
          ...commonFields,
          <Form.Item
            label="Content"
            name="content"
            key="content"
            rules={[{ required: true, message: 'Please input the content!' }]}
          >
            <ReactQuill
              theme="snow"
              value={quillContent}
              onChange={(value) => {
                setQuillContent(value);
                form.setFieldsValue({ content: value });
              }}
              style={{ height: '300px', marginBottom: '40px' }}
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  ['link', 'image'],
                  ['clean']
                ]
              }}
            />
          </Form.Item>
        ];
      case 'careers':
        return [
          ...commonFields,
          <Form.Item
            name="job_type"
            label="Job Type"
            key="job_type"
            rules={[{ required: true, message: 'Please input the job type!' }]}
          >
            <Select>
              <Option value="Full-time">Full-time</Option>
              <Option value="Part-time">Part-time</Option>
              <Option value="Contract">Contract</Option>
              <Option value="Internship">Internship</Option>
            </Select>
          </Form.Item>,
          <Form.Item
            name="location"
            label="Location"
            key="location"
            rules={[{ required: true, message: 'Please input the location!' }]}
          >
            <Input />
          </Form.Item>,
          <Form.Item
            name="description"
            label="Description"
            key="description"
            rules={[{ required: true, message: 'Please input the description!' }]}
          >
            <ReactQuill
              theme="snow"
              onChange={(value) => form.setFieldsValue({ description: value })}
              style={{ height: '200px', marginBottom: '40px' }}
            />
          </Form.Item>,
          <Form.Item
            name="requirements"
            label="Requirements"
            key="requirements"
          >
            <ReactQuill
              theme="snow"
              onChange={(value) => form.setFieldsValue({ requirements: value })}
              style={{ height: '200px', marginBottom: '40px' }}
            />
          </Form.Item>,
          <Form.Item
            name="responsibilities"
            label="Responsibilities"
            key="responsibilities"
          >
            <ReactQuill
              theme="snow"
              onChange={(value) => form.setFieldsValue({ responsibilities: value })}
              style={{ height: '200px', marginBottom: '40px' }}
            />
          </Form.Item>
        ];
      case 'faqs':
        return [
          ...commonFields,
          <Form.Item
            name="category"
            label="Category"
            key="category"
            rules={[{ required: true, message: 'Please input the category!' }]}
          >
            <Input />
          </Form.Item>,
          <Form.Item
            name="question"
            label="Question"
            key="question"
            rules={[{ required: true, message: 'Please input the question!' }]}
          >
            <Input />
          </Form.Item>,
          <Form.Item
            name="answer"
            label="Answer"
            key="answer"
            rules={[{ required: true, message: 'Please input the answer!' }]}
          >
            <ReactQuill
              theme="snow"
              onChange={(value) => form.setFieldsValue({ answer: value })}
              style={{ height: '200px', marginBottom: '40px' }}
            />
          </Form.Item>
        ];
      case 'team_members':
        return [
          ...commonFields,
          <Form.Item
            name="name"
            label="Name"
            key="name"
            rules={[{ required: true, message: 'Please input the name!' }]}
          >
            <Input />
          </Form.Item>,
          <Form.Item
            name="position"
            label="Position"
            key="position"
            rules={[{ required: true, message: 'Please input the position!' }]}
          >
            <Input />
          </Form.Item>,
          <Form.Item
            name="bio"
            label="Bio"
            key="bio"
          >
            <ReactQuill
              theme="snow"
              onChange={(value) => form.setFieldsValue({ bio: value })}
              style={{ height: '200px', marginBottom: '40px' }}
            />
          </Form.Item>,
          <Form.Item
            name="profile_image"
            label="Profile Image"
            key="profile_image"
            hidden
          >
            <Input />
          </Form.Item>,
          <Form.Item label="Upload Profile Image">
            <Upload {...uploadProps} maxCount={1}>
              <Button icon={<UploadOutlined />} loading={uploading}>
                {uploading ? 'Uploading...' : 'Click to Upload'}
              </Button>
            </Upload>
          </Form.Item>,
          <Divider orientation="left">Social Links</Divider>,
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={['social_links', 'email']}
                label="Email"
                key="email"
              >
                <Input prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['social_links', 'phone']}
                label="Phone"
                key="phone"
              >
                <Input prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>
          </Row>,
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={['social_links', 'facebook']}
                label="Facebook"
                key="facebook"
              >
                <Input prefix={<FacebookOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['social_links', 'twitter']}
                label="Twitter"
                key="twitter"
              >
                <Input prefix={<TwitterOutlined />} />
              </Form.Item>
            </Col>
          </Row>,
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={['social_links', 'linkedin']}
                label="LinkedIn"
                key="linkedin"
              >
                <Input prefix={<LinkedinOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['social_links', 'instagram']}
                label="Instagram"
                key="instagram"
              >
                <Input prefix={<InstagramOutlined />} />
              </Form.Item>
            </Col>
          </Row>
        ];
      case 'newsletters':
        return [
          ...commonFields,
          <Form.Item
            name="description"
            label="Description"
            key="description"
          >
            <ReactQuill
              theme="snow"
              onChange={(value) => form.setFieldsValue({ description: value })}
              style={{ height: '200px', marginBottom: '40px' }}
            />
          </Form.Item>,
          <Form.Item
            name="cta_text"
            label="CTA Text"
            key="cta_text"
          >
            <Input />
          </Form.Item>
        ];
      case 'social_links':
        return [
          <Form.Item
            name="platform"
            label="Platform"
            key="platform"
            rules={[{ required: true, message: 'Please select the platform!' }]}
          >
            <Select>
              <Option value="facebook">Facebook</Option>
              <Option value="twitter">Twitter</Option>
              <Option value="instagram">Instagram</Option>
              <Option value="linkedin">LinkedIn</Option>
              <Option value="youtube">YouTube</Option>
              <Option value="pinterest">Pinterest</Option>
            </Select>
          </Form.Item>,
          <Form.Item
            name="url"
            label="URL"
            key="url"
            rules={[{ required: true, message: 'Please input the URL!' }]}
          >
            <Input prefix={<LinkOutlined />} />
          </Form.Item>,
          <Form.Item
            name="is_active"
            label="Active"
            key="is_active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        ];
      case 'videos':
        return [
          ...commonFields,
          <Form.Item
            name="video_url"
            label="Video URL"
            key="video_url"
            rules={[{ required: true, message: 'Please input the video URL!' }]}
          >
            <Input prefix={<PlayCircleOutlined />} />
          </Form.Item>,
          <Form.Item
            name="description"
            label="Description"
            key="description"
          >
            <ReactQuill
              theme="snow"
              onChange={(value) => form.setFieldsValue({ description: value })}
              style={{ height: '200px', marginBottom: '40px' }}
            />
          </Form.Item>
        ];
      case 'testimonials':
        return [
          ...commonFields,
          <Form.Item
            name="name"
            label="Person Name"
            key="name"
            rules={[{ required: true, message: 'Please input the name!' }]}
          >
            <Input />
          </Form.Item>,
          // REMOVED: designation field
          // REMOVED: company field
          // REMOVED: user_id field
          <Form.Item
            name="status"
            label="Status"
            key="status"
            rules={[{ required: true, message: 'Please select status!' }]}
            initialValue="pending"
          >
            <Select>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
              <Option value="pending">Pending</Option>
            </Select>
          </Form.Item>,
          <Form.Item
            name="message"
            label="Testimonial"
            key="message"
            rules={[{ required: true, message: 'Please input the testimonial!' }]}
          >
            <ReactQuill
              theme="snow"
              onChange={(value) => form.setFieldsValue({ message: value })}
              style={{ height: '200px', marginBottom: '40px' }}
            />
          </Form.Item>,
          <Form.Item
            name="profile_image"
            label="Profile Image"
            key="profile_image"
            hidden
          >
            <Input />
          </Form.Item>,
          <Form.Item label="Upload Profile Image">
            <Upload {...uploadProps} maxCount={1}>
              <Button icon={<UploadOutlined />} loading={uploading}>
                {uploading ? 'Uploading...' : 'Click to Upload'}
              </Button>
            </Upload>
          </Form.Item>
        ];
      case 'blogs':
        return [
          ...commonFields,
          <Form.Item
            name="author"
            label="Author"
            key="author"
            rules={[{ required: true, message: 'Please input the author!' }]}
          >
            <Input />
          </Form.Item>,
          <Form.Item
            name="slug"
            label="Slug"
            key="slug"
            rules={[{ required: true, message: 'Please input the slug!' }]}
          >
            <Input />
          </Form.Item>,
          <Form.Item
            name="excerpt"
            label="Excerpt"
            key="excerpt"
          >
            <ReactQuill
              theme="snow"
              onChange={(value) => form.setFieldsValue({ excerpt: value })}
              style={{ height: '150px', marginBottom: '40px' }}
            />
          </Form.Item>,
          <Form.Item
            name="content"
            label="Content"
            key="content"
            rules={[{ required: true, message: 'Please input the content!' }]}
          >
            <ReactQuill
              theme="snow"
              onChange={(value) => form.setFieldsValue({ content: value })}
              style={{ height: '300px', marginBottom: '40px' }}
            />
          </Form.Item>,
          <Form.Item
            name="tags"
            label="Tags"
            key="tags"
          >
            <Select mode="tags" tokenSeparators={[',']} />
          </Form.Item>,
          <Form.Item
            name="is_published"
            label="Published"
            key="is_published"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>,
          <Form.Item
            name="image_url"
            label="Featured Image"
            key="image_url"
            hidden
          >
            <Input />
          </Form.Item>,
          <Form.Item label="Upload Featured Image">
            <Upload {...uploadProps} maxCount={1}>
              <Button icon={<UploadOutlined />} loading={uploading}>
                {uploading ? 'Uploading...' : 'Click to Upload'}
              </Button>
            </Upload>
          </Form.Item>
        ];
      case 'banners':
        return [
          ...commonFields,
          <Form.Item
            name="link_url"
            label="Link URL"
            key="link_url"
          >
            <Input />
          </Form.Item>,
          <Form.Item
            name="position"
            label="Position"
            key="position"
          >
            <Select>
              <Option value="top">Top</Option>
              <Option value="middle">Middle</Option>
              <Option value="bottom">Bottom</Option>
            </Select>
          </Form.Item>,
          <Form.Item
            name="image_url"
            label="Banner Image"
            key="image_url"
            hidden
          >
            <Input />
          </Form.Item>,
          <Form.Item label="Upload Banner Image">
            <Upload {...uploadProps} maxCount={1}>
              <Button icon={<UploadOutlined />} loading={uploading}>
                {uploading ? 'Uploading...' : 'Click to Upload'}
              </Button>
            </Upload>
          </Form.Item>
        ];
      default:
        return commonFields;
    }
  };

  function capitalize(str) {
    if (!str) return '';
    return str.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  const getTabLabel = (type) => {
    const labels = {
      privacy_policy: 'Privacy Policy',
      terms_conditions: 'Terms & Conditions',
        refund_policy: 'Refund Policy',
      team_members: 'Our Team',
      social_links: 'Social Links'
    };
    return labels[type] || capitalize(type);
  };

  return (
    <>
     <Helmet>
        <title>Content Management</title>
      </Helmet>
      <div className="content-management" style={{ padding: '20px' }}>
        {successMsg && (
          <Alert
            style={{ marginBottom: 16 }}
            message={successMsg}
            type="success"
            showIcon
            closable
            onClose={() => setSuccessMsg('')}
          />
        )}
        {errorMsg && (
          <Alert
            style={{ marginBottom: 16 }}
            message={errorMsg}
            type="error"
            showIcon
            closable
            onClose={() => setErrorMsg('')}
          />
        )}
    
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          {contentTypes.map(type => (
            <TabPane tab={getTabLabel(type)} key={type}>
              {!['privacy_policy', 'terms_conditions', 'refund_policy'].includes(type) && (
                <Button
                  type="primary"
                  onClick={() => {
                    form.resetFields();
                    setFileList([]);
                    setEditingId(null);
                    setQuillContent('');
                    setOpen(true);
                  }}
                  style={{ marginBottom: 16 }}
                >
                  Add New
                </Button>
              )}

              <Table
                ref={tableRef}
                columns={getColumns()}
                dataSource={content[type] || []}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
                components={{
                  body: {
                    row: (props) => <tr {...props} />,
                  },
                }}
              />
            </TabPane>
          ))}
        </Tabs>

        <Modal
          title={editingId ? `Edit ${getTabLabel(activeTab)}` : `Create ${getTabLabel(activeTab)}`}
          open={open}
          onOk={handleSubmit}
          onCancel={() => {
            setOpen(false);
            setFileList([]);
            setQuillContent('');
          }}
          confirmLoading={loading || uploading}
          width={activeTab === 'team_members' ? 1000 : 800}
          destroyOnClose
        >
          <Form form={form} layout="vertical">
            {getFormFields()}
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default ContentManagement;